import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import {
  FormContext,
  metaBuyingFormDefault,
  metaCampaignFormDefault,
  metaAdSetFormDefault,
} from "../MetaBuyingContext";
import { MetaBuyingNamePreview } from "./MetaBuyingNamePreview";
import { MetaBuyingOptionsNoteCard } from "./MetaBuyingOptionsNoteCard";
import { Form, DropdownButton, Dropdown } from "react-bootstrap";
import { MetaBuyingFooterControls } from "./MetaBuyingFooterControls";
import { Button, ButtonType } from "../../Components/Button/Button";
import { Spinner } from "../../Components/Spinner";
import { RouteComponentProps } from "@reach/router";
import {
  filterSegmentationIds,
  generateNameFields,
  filterInheritedFields,
  mapRowsByProp,
  validateName,
  mapLabelsByValues,
  getAttributionOptions,
  removeIsoTZ,
  MissingCreativeWarning,
  getAssetValues,
} from "../MetaBuyingUtils";
import { MetaBuyingTabs } from "../MetaBuyingTabs";
import { ButtonFrameworkVariant } from "../../Components/ButtonFramework/ButtonFramework";
import { awaitJSON, MetaLambdaFetch } from "../../utils/fetch-utils";
import {
  AdSetRow,
  CampaignRow,
  Dimension,
  CreationTab,
  FormField,
  Level,
  ConversionGoal,
  PlatformPublisher,
  Position,
  Gender,
  BidConstraint,
  BidStrategy,
  ConversionEvent,
  AdsManagerStatus,
  Objective,
  SpecialAdCategory,
  MetaBuyingImage,
  MetaBuyingVideo,
  AdAssetPlacementGroup,
  AdCreativeFormat,
} from "@blisspointmedia/bpm-types/dist/MetaBuying";
import { ModalError, useSetError } from "../../redux/modals";
import * as R from "ramda";
import * as Dfns from "date-fns/fp";
import MetaBuyingNameFields from "./MetaBuyingNameFields";
import { MetaCreativeAssetModal } from "./MetaCreativeAssetModal";
import { HiPlus } from "react-icons/hi";
import { MdDelete } from "react-icons/md";
import {
  TAB_AD_SET,
  CAMPAIGN,
  AD_SET,
  AD,
  VIDEO,
  STATUS_NONE,
  OUTCOME_SALES,
  ALL,
  CAMPAIGN_DAILY_BUDGET,
  CAMPAIGN_LIFETIME_BUDGET,
  AD_SET_DAILY_BUDGET,
  AD_SET_LIFETIME_BUDGET,
  CTA_OPTIONS,
  CREATIVE_TYPES,
  OPTIONS_NOTE_CARD_HEADERS,
  OPTIONS_NOTE_CARD_OPTIONS,
  CONVERSION_GOALS,
  COST_CAP,
  LOWEST_COST_WITH_BID_CAP,
  LOWEST_COST_WITH_MIN_ROAS,
  OBJECTIVES,
  DROPDOWN_BTN,
  PLACEMENT_GROUP_OPTIONS,
  SQUARE,
  IMAGE,
} from "../MetaBuyingConstants";
import { PageTab } from "../MetaBuying";
import { SingleSelectMenu } from "./Menus/SingleSelectMenu";

interface MetaBuyingAdProps {
  client: string;
  namingConventions: {
    [CAMPAIGN]: number[];
    [AD_SET]: number[];
    [AD]: number[];
  };
  tabIndex: number;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  showReviewModal: () => void;
  selectedAdAccount: {
    account_id: string;
    account_name: string;
    business_manager: string;
  };
  validateForms: (
    metaBuyingForm: typeof metaBuyingFormDefault,
    navigate: () => void,
    nameFields: Record<string, any>,
    tabToValidate?: CreationTab
  ) => void;
  creationTabs: CreationTab[];
  granularity: Level;
  dimensions: Record<string, any>;
  allNameFields: Record<string, any>;
  setAllNameFields: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  adSets: AdSetRow[];
  campaigns: CampaignRow[];
  facebookPages: { page_id: string; name: string }[];
  // instagramAccounts: { id: string; username: string }[];
  saveNameFields: (nameFields: Record<string, any>) => void;
  goToTables: (tab: PageTab) => void;
  adVideoThumbnail: string;
}

export const MetaBuyingAd = ({
  client,
  selectedAdAccount,
  namingConventions,
  showReviewModal,
  validateForms,
  creationTabs,
  granularity,
  dimensions,
  tabIndex,
  setTabIndex,
  allNameFields,
  setAllNameFields,
  adSets,
  campaigns,
  facebookPages,
  // instagramAccounts,
  saveNameFields,
  goToTables,
  adVideoThumbnail,
}: MetaBuyingAdProps & RouteComponentProps): JSX.Element => {
  const { metaBuyingForm, setMetaBuyingForm } = useContext(FormContext);

  const metaAdForm = metaBuyingForm.ad;

  const namingConvention = namingConventions[AD];

  const { account_id, business_manager } = selectedAdAccount;

  const setError = useSetError();

  const ADSET_MENU_ID = "adSetMenu";

  const segmentationDimensionId = R.keys(
    R.filter(dimension => dimension.type === Dimension.SEGMENTATION_ID, dimensions)
  )[0];

  const isSegmentedLevel = R.includes(Number(segmentationDimensionId), namingConvention);

  const [nameFields, setNameFields] = useState<Record<string, any>>(
    generateNameFields(namingConvention, allNameFields)
  );

  const [enteredAdSetId, setEnteredAdSetId] = useState("");
  const [showInputAdSetId, setShowInputAdSetId] = useState(false);
  const [menusState, setMenusState] = useState<Record<string, any>>({
    [ADSET_MENU_ID]: false,
  });
  const [adSetLoading, setAdSetLoading] = useState(false);
  const [creativeModalType, setCreativeModalType] = useState<AdCreativeFormat | null>(null);
  const [defaultAssetFrame, setDefaultAssetFrame] = useState<AdAssetPlacementGroup>(SQUARE);
  const [videoAssets, setVideoAssets] = useState<Record<string, any> | null>(null);
  const [imageAssets, setImageAssets] = useState<Record<string, any> | null>(null);
  const [loadingVideos, setLoadingVideos] = useState<boolean>(false);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [savedAssetsLoaded, setSavedAssetsLoaded] = useState<boolean>(!metaAdForm.ad_id);

  const inheritedFields = filterInheritedFields(allNameFields);

  const setNameField = useCallback(
    (id: string, value: string, updateType: "state" | "context" | "all"): void => {
      if (R.includes(updateType, ["context", "all"])) {
        let newNameFields = R.clone(allNameFields);
        newNameFields[id].value = value;
        setAllNameFields(newNameFields);
      }
      if (R.includes(updateType, ["state", "all"])) {
        let newNameFields = R.clone(nameFields);
        newNameFields[id] = value;
        setNameFields(newNameFields);
      }
    },
    [nameFields, allNameFields, setAllNameFields]
  );

  const setMetaCampaignForm = useCallback(
    value =>
      setMetaBuyingForm((current: any): typeof metaBuyingFormDefault => {
        return { ...current, campaign: value };
      }),
    [setMetaBuyingForm]
  );

  const setMetaAdSetForm = useCallback(
    value =>
      setMetaBuyingForm((current: any): typeof metaBuyingFormDefault => {
        return { ...current, ad_set: value };
      }),
    [setMetaBuyingForm]
  );

  const setMetaAdForm = useCallback(
    value =>
      setMetaBuyingForm((current: any): typeof metaBuyingFormDefault => {
        return { ...current, ad: value };
      }),
    [setMetaBuyingForm]
  );

  const updateFormField = useCallback(
    (key: string, value: FormField) =>
      setMetaBuyingForm((current: any): typeof metaBuyingFormDefault => {
        return { ...current, ad: { ...current.ad, [key]: value } };
      }),
    [setMetaBuyingForm]
  );

  /**
   * Reset segmentation ID field if segments selected have changed
   */
  useEffect(() => {
    if (isSegmentedLevel) {
      // @ts-ignore
      const fields = R.map(({ value }) => value, allNameFields);
      // @ts-ignore
      const filteredSegIds = filterSegmentationIds(fields, dimensions, segmentationDimensionId);
      const selectedSegId = fields[segmentationDimensionId];
      if (!R.includes(selectedSegId, filteredSegIds) && !R.isEmpty(selectedSegId)) {
        setNameField(String(segmentationDimensionId), "", "state");
        setAllNameFields({ ...allNameFields, [segmentationDimensionId]: { value: "" } });
      }
    }
  }, [
    allNameFields,
    segmentationDimensionId,
    dimensions,
    setNameField,
    setAllNameFields,
    isSegmentedLevel,
  ]);

  useEffect(() => {
    const newAdName = namingConvention.map(dimensionId => nameFields[dimensionId]).join("_");
    if (!R.equals(newAdName, metaAdForm.ad_name) && metaAdForm.ad_id === null) {
      updateFormField("ad_name", newAdName);
    }
  }, [nameFields, updateFormField, namingConvention, metaAdForm]);

  useEffect(() => {
    if (metaAdForm.ad_id !== null && R.all(value => R.isEmpty(value), R.values(nameFields))) {
      let valid = validateName(
        metaAdForm.ad_name,
        AD,
        namingConventions,
        dimensions,
        client,
        business_manager
      );
      if (valid) {
        let newNameFields = R.clone(nameFields);

        const thisNameElements = metaAdForm.ad_name.split("_");

        namingConvention.forEach((dimensionId, index) => {
          newNameFields[dimensionId] = thisNameElements[index];
        });

        setNameFields(newNameFields);
      } else {
        setError({
          title: "Ad Name Error",
          message: `Unable to edit ad '${metaAdForm.ad_name}' as it does not follow the ${client} ad naming convention`,
          okayText: "Return to Drafts",
          onOkay: () => goToTables(PageTab.DRAFTS),
        });
      }
    }
  }, [
    dimensions,
    nameFields,
    namingConvention,
    inheritedFields,
    business_manager,
    metaAdForm,
    namingConventions,
    goToTables,
    client,
    setError,
  ]);

  useEffect(() => {
    const handleOutsideClick = event => {
      const closestMenuToggle = event.target.closest(".menuToggle");
      const closestAppliedSelection = event.target.closest(".appliedSelection");

      const idTokens = closestMenuToggle ? closestMenuToggle.id.split("_") : null;
      const menuId: string | null = idTokens ? idTokens[0] : null;
      const nodeType: string | null = idTokens ? idTokens[1] : null;

      if (menuId && nodeType === DROPDOWN_BTN && R.isNil(closestAppliedSelection)) {
        //@ts-ignore
        setMenusState(
          R.fromPairs(
            R.map(([key, value]) => [key, key === menuId ? !value : false], R.toPairs(menusState))
          )
        );
        //@ts-ignore
      } else if (!menuId && R.any(menuState => menuState === true, R.values(menusState))) {
        //@ts-ignore
        setMenusState(R.map(() => false, menusState));
        //@ts-ignore
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [menusState]);

  const applyAdSet = useCallback(
    (adSet: AdSetRow, parentCampaign?: CampaignRow) => {
      try {
        const campaign = parentCampaign ?? mapRowsByProp("id", campaigns)[adSet.campaign_id];

        if (R.isNil(campaign)) {
          throw new Error(`Invalid campaign ID: '${adSet.campaign_id}'`);
        }

        /*
         * Ignore name validation checks when the campaign and/or ad set is imported
         * from Ads Manager.
         */
        const isCampaignImported = campaign.lastuser === "Ads Manager";
        const isAdSetImported = adSet.lastuser === "Ads Manager";
        if (
          !validateName(
            adSet.adset_name,
            AD_SET,
            namingConventions,
            dimensions,
            client,
            business_manager,
            granularity === CAMPAIGN ? CAMPAIGN : undefined,
            campaign.campaign_name,
            isAdSetImported
          )
        ) {
          throw new Error(
            `Ad Set name '${adSet.adset_name}' does not follow this client's naming convention`
          );
        } else if (
          !validateName(
            campaign.campaign_name,
            CAMPAIGN,
            namingConventions,
            dimensions,
            client,
            business_manager,
            undefined,
            undefined,
            isCampaignImported
          )
        ) {
          throw new Error(
            `Campaign name '${campaign.campaign_name}' does not follow ${client}'s naming convention`
          );
        }

        let newAllNameFields = R.clone(allNameFields);
        let newNameFields = R.clone(nameFields);
        let campaignNameElements = campaign.campaign_name.split("_");
        let adSetNameElements = adSet.adset_name.split("_");

        /*
         * If the campaign and/or ad set is imported from Ads Manager, the
         * campaign/ad set name might not contain a segmentation id, which
         * our naming convention requires. In this case, depending on the
         * client's granularity we add a dummy value "TINSEGID" to the name
         * fields in order to avoid downstream name validation errors.
         */
        let nameIndex = 0;
        namingConventions[CAMPAIGN].forEach(dimensionId => {
          const dimension = dimensions[dimensionId];
          let value;
          if (
            dimension.type === Dimension.SEGMENTATION_ID &&
            !campaignNameElements[nameIndex]?.includes("TINSEGID") &&
            isCampaignImported
          ) {
            value = "TINSEGID";
          } else {
            value = campaignNameElements[nameIndex];
            nameIndex++;
          }

          newAllNameFields[dimensionId] = { value };
          if (allNameFields[dimensionId].inheritedFrom) {
            newAllNameFields[dimensionId].inheritedFrom = allNameFields[dimensionId].inheritedFrom;
          }
          if (R.includes(dimensionId, namingConventions[AD])) {
            newNameFields[dimensionId] = value;
          }
        });

        nameIndex = 0;
        namingConventions[AD_SET].forEach(dimensionId => {
          const dimension = dimensions[dimensionId];
          let value;
          if (
            dimension.type === Dimension.SEGMENTATION_ID &&
            !adSetNameElements[nameIndex]?.includes("TINSEGID") &&
            isAdSetImported
          ) {
            value = "TINSEGID";
          } else {
            value = adSetNameElements[nameIndex];
            nameIndex++;
          }

          newAllNameFields[dimensionId] = { value };
          if (allNameFields[dimensionId].inheritedFrom) {
            newAllNameFields[dimensionId].inheritedFrom = allNameFields[dimensionId].inheritedFrom;
          }
          if (R.includes(dimensionId, namingConventions[AD])) {
            newNameFields[dimensionId] = value;
          }
        });

        let newMetaCampaignForm = R.clone(metaCampaignFormDefault);
        let newMetaAdSetForm = R.clone(metaAdSetFormDefault);

        newMetaCampaignForm.campaign_name = campaign.campaign_name ?? "";
        newMetaCampaignForm.status =
          (campaign.adsmanager_status as AdsManagerStatus) ?? STATUS_NONE;
        newMetaCampaignForm.meta_id = campaign.meta_id ?? null;
        newMetaCampaignForm.bid_strategy = (campaign.bid_strategy as BidStrategy) ?? null;
        newMetaCampaignForm.objective = (campaign.objective as Objective) ?? OUTCOME_SALES;

        if (!R.includes(newMetaCampaignForm.objective, OBJECTIVES)) {
          throw new Error(
            `Campaign has an objective of type '${newMetaCampaignForm.objective}', which is not currently supported`
          );
        }

        newMetaCampaignForm.special_ad_categories =
          (campaign.special_ad_categories as SpecialAdCategory[]) ?? [];

        if (campaign.daily_budget) {
          newMetaCampaignForm.budget = Number(campaign.daily_budget);
          newMetaCampaignForm.budget_type = CAMPAIGN_DAILY_BUDGET;
        } else if (campaign.lifetime_budget) {
          newMetaCampaignForm.budget = Number(campaign.lifetime_budget);
          newMetaCampaignForm.budget_type = CAMPAIGN_LIFETIME_BUDGET;
        }

        newMetaAdSetForm.adset_name = adSet.adset_name ?? "";
        newMetaAdSetForm.campaign_id = campaign.id ?? null;
        newMetaAdSetForm.status = (adSet.adsmanager_status as AdsManagerStatus) ?? STATUS_NONE;
        newMetaAdSetForm.meta_id = adSet.meta_id ?? null;
        newMetaAdSetForm.pixel = adSet.pixel_id ?? null;
        newMetaAdSetForm.conversion_event = (adSet.custom_event_type as ConversionEvent) ?? null;
        newMetaAdSetForm.optimization_goal = (adSet.optimization_goal as ConversionGoal) ?? null;
        newMetaAdSetForm.bid_strategy = (adSet.bid_strategy as BidStrategy) ?? null;
        newMetaAdSetForm.bid_amount = Number(adSet.bid_amount) || null;
        newMetaAdSetForm.bid_constraints = adSet.roas_average_floor
          ? ({ roas_average_floor: adSet.roas_average_floor } as BidConstraint)
          : {};
        newMetaAdSetForm.genders = (adSet.genders as Gender) ?? ALL;
        newMetaAdSetForm.age_min = Number(adSet.age_min) || 18;
        newMetaAdSetForm.age_max = Number(adSet.age_max) || 65;
        newMetaAdSetForm.click_attr_window = Number(adSet.click_attribution_window) || 1;
        newMetaAdSetForm.view_attr_window = Number(adSet.view_attribution_window) || 0;
        newMetaAdSetForm.publisher_platforms =
          (adSet.publisher_platforms as PlatformPublisher[]) ?? [];
        newMetaAdSetForm.facebook_positions = (adSet.facebook_positions as Position[]) ?? [];
        newMetaAdSetForm.instagram_positions = (adSet.instagram_positions as Position[]) ?? [];
        newMetaAdSetForm.messenger_positions = (adSet.messenger_positions as Position[]) ?? [];
        newMetaAdSetForm.audience_network_positions =
          (adSet.audience_network_positions as Position[]) ?? [];
        newMetaAdSetForm.start_time = adSet.start_time ?? null;
        newMetaAdSetForm.end_time = adSet.end_time ?? null;

        if (adSet.daily_budget) {
          newMetaAdSetForm.budget = Number(adSet.daily_budget);
          newMetaAdSetForm.budget_type = AD_SET_DAILY_BUDGET;
        } else if (adSet.lifetime_budget) {
          newMetaAdSetForm.budget = Number(adSet.lifetime_budget);
          newMetaAdSetForm.budget_type = AD_SET_LIFETIME_BUDGET;
        }

        if (!newMetaAdSetForm.optimization_goal) {
          throw new Error("Ad set is missing an optimization goal");
        } else if (!newMetaAdSetForm.conversion_event) {
          throw new Error("Ad set is missing a conversion event");
        } else if (!newMetaAdSetForm.bid_strategy && !newMetaCampaignForm.bid_strategy) {
          throw new Error("Neither the ad set nor its parent campaign have a bid strategy set");
        } else {
          const { validOptions } = CONVERSION_GOALS[newMetaAdSetForm.optimization_goal];
          const { objectives, bidStrategies } = validOptions;
          if (
            !R.includes(newMetaCampaignForm.objective, objectives) ||
            (newMetaAdSetForm.bid_strategy &&
              !R.includes(newMetaAdSetForm.bid_strategy, bidStrategies))
          ) {
            throw new Error("Invalid conversion options");
          }

          if (
            (newMetaAdSetForm.bid_strategy === COST_CAP ||
              newMetaAdSetForm.bid_strategy === LOWEST_COST_WITH_BID_CAP) &&
            !newMetaAdSetForm.bid_amount
          ) {
            throw new Error("No bid amount set for bid strategy");
          }

          if (
            newMetaAdSetForm.bid_strategy === LOWEST_COST_WITH_MIN_ROAS &&
            !newMetaAdSetForm.bid_constraints.roas_average_floor
          ) {
            throw new Error("No min ROAS set for bid strategy");
          }

          const { click_through, view_through } = getAttributionOptions(
            newMetaCampaignForm.objective,
            newMetaAdSetForm.optimization_goal
          );

          if (!R.includes(newMetaAdSetForm.click_attr_window, click_through)) {
            throw new Error("Invalid click through attribution window");
          }
          if (!R.includes(newMetaAdSetForm.view_attr_window, view_through)) {
            throw new Error("Invalid view through attribution window");
          }
        }

        if (
          (campaign.daily_budget || campaign.lifetime_budget) &&
          (adSet.daily_budget || adSet.lifetime_budget)
        ) {
          throw new Error("Both campaign and ad set have budgets set");
        }

        if (newMetaCampaignForm.status === STATUS_NONE && newMetaAdSetForm.status !== STATUS_NONE) {
          throw new Error("Invalid campaign status");
        }

        // const allPositions = [
        //   ...newMetaAdSetForm.facebook_positions,
        //   ...newMetaAdSetForm.instagram_positions,
        //   ...newMetaAdSetForm.audience_network_positions,
        //   ...newMetaAdSetForm.messenger_positions,
        // ];
        // if (!allPositions.length || !newMetaAdSetForm.publisher_platforms.length) {
        //   throw new Error("Ad set placements missing");
        // }

        if (
          newMetaAdSetForm.start_time &&
          newMetaAdSetForm.end_time &&
          Dfns.isAfter(
            Dfns.parseISO(removeIsoTZ(newMetaAdSetForm.end_time)),
            Dfns.parseISO(removeIsoTZ(newMetaAdSetForm.start_time))
          )
        ) {
          throw new Error("End date is before start date");
        }

        setNameFields(newNameFields);
        setAllNameFields(newAllNameFields);
        setMetaCampaignForm(newMetaCampaignForm);
        setMetaAdSetForm(newMetaAdSetForm);
        updateFormField("adset_id", adSet.id);
      } catch (e) {
        setError({
          title: "Invalid Ad Set",
          message: (e as ModalError).message,
          reportError: (e as ModalError).reportError,
        });
      }
    },
    [
      allNameFields,
      business_manager,
      campaigns,
      client,
      dimensions,
      granularity,
      nameFields,
      namingConventions,
      setAllNameFields,
      setError,
      setMetaAdSetForm,
      setMetaCampaignForm,
      updateFormField,
    ]
  );

  const removeAdSet = () => {
    setMetaAdSetForm(metaAdSetFormDefault);
    setMetaCampaignForm(metaCampaignFormDefault);
    setNameFields(
      R.fromPairs(
        R.map(
          ([key, value]) => [key, R.includes(Number(key), inheritedFields) ? "" : value],
          R.toPairs(nameFields)
        )
      )
    );
    setEnteredAdSetId("");
    updateFormField("adset_id", null);
  };

  const submitAdSetId = useCallback(
    async id => {
      setAdSetLoading(true);

      try {
        let res = await MetaLambdaFetch("/getAdSetInfo", {
          params: {
            accountId: account_id,
            adSetId: id,
            businessManager: business_manager,
          },
        });
        let adSetInfo: { campaign: CampaignRow; adSet: AdSetRow } = await awaitJSON(res);

        if (R.isNil(adSetInfo) || R.isEmpty(adSetInfo)) {
          setError({
            title: "Failed to Fetch Ad Set",
            message: `No ad sets found with ID ${id} in account ${account_id}`,
          });
        } else {
          const { campaign: parentCampaign, adSet: importedAdSet } = adSetInfo;
          applyAdSet(importedAdSet, parentCampaign);
        }
      } catch (e) {
        setError({
          message: (e as ModalError).message,
          reportError: (e as ModalError).reportError,
        });
      } finally {
        setAdSetLoading(false);
      }
    },
    [account_id, applyAdSet, business_manager, setError]
  );

  const toggleMenuState = (menuId, newStatus?) =>
    setMenusState({
      ...menusState,
      [menuId]: !R.isNil(newStatus) ? newStatus : !menusState[menuId],
    });

  const closeMenuState = menuId => toggleMenuState(menuId, false);

  const InputNote = ({ noteText, buttonText, switchInput }): JSX.Element => (
    <p className="metaBuyingInputNote">
      {noteText}&nbsp;
      <button onClick={() => switchInput()} className="metaBuyingInputSwitch">
        {buttonText}
      </button>
    </p>
  );

  const switchInput = () => {
    removeAdSet();
    setShowInputAdSetId(!showInputAdSetId);
  };

  // const instagramAccountMap = mapRowsByProp("id", instagramAccounts)[
  //   metaAdForm.instagram_account_id
  // ];

  const handleChooseAsset = frame => {
    setDefaultAssetFrame(frame);
    setCreativeModalType(metaAdForm.creative_format);
  };

  const selectedAssets = useMemo(() => getAssetValues(metaAdForm), [metaAdForm]);

  const creativeType = useMemo(() => (metaAdForm.creative_format === IMAGE ? "image" : "video"), [
    metaAdForm,
  ]);

  useEffect(() => {
    if (metaAdForm.ad_id) {
      if (R.isNil(videoAssets) && !loadingVideos) {
        (async () => {
          try {
            setLoadingVideos(true);
            const videosRes = await MetaLambdaFetch("/getMetaBuyingVideos", {
              params: { accountIDs: account_id },
            });
            const videoAssetsData = await awaitJSON(videosRes);

            setVideoAssets(R.indexBy(R.prop("video_id"), videoAssetsData[account_id]));
          } catch (e) {
            const error = e as Error;
            setError({
              message: `Error fetching meta videos for accountId: ${account_id}, Error: ${error.message}`,
            });
          } finally {
            setLoadingVideos(false);
          }
        })();
      }
      if (R.isNil(imageAssets) && !loadingImages) {
        (async () => {
          try {
            setLoadingImages(true);
            const imagesRes = await MetaLambdaFetch("/getMetaBuyingImages", {
              params: { accountIDs: account_id },
            });
            const imageAssetsData = await awaitJSON(imagesRes);

            setImageAssets(R.indexBy(R.prop("hash"), imageAssetsData[account_id]));
          } catch (e) {
            const error = e as Error;
            setError({
              message: `Error fetching meta images for accountId: ${account_id}, Error: ${error.message}`,
            });
          } finally {
            setLoadingImages(false);
          }
        })();
      }
    }
  }, [metaAdForm, videoAssets, imageAssets, loadingVideos, loadingImages, account_id, setError]);

  useEffect(() => {
    if (metaAdForm.ad_id && !savedAssetsLoaded && videoAssets && imageAssets) {
      let newMetaAdForm = R.clone(metaAdForm);

      PLACEMENT_GROUP_OPTIONS.forEach(({ value }) => {
        newMetaAdForm[`creative_asset_${value}`] = null;

        if (
          metaAdForm[`creative_asset_${value}`] &&
          metaAdForm[`creative_asset_${value}`].video_id
        ) {
          if (R.isNil(videoAssets[metaAdForm[`creative_asset_${value}`].video_id])) {
            setError({
              message: `Failed to fetch video with ID: ${account_id}`,
            });
          } else {
            newMetaAdForm[`creative_asset_${value}`] =
              videoAssets[metaAdForm[`creative_asset_${value}`].video_id];
          }
        }

        if (metaAdForm[`creative_asset_${value}`] && metaAdForm[`creative_asset_${value}`].hash) {
          if (R.isNil(imageAssets[metaAdForm[`creative_asset_${value}`].hash])) {
            setError({
              message: `Failed to fetch image with hash: ${account_id}`,
            });
          } else {
            newMetaAdForm[`creative_asset_${value}`] =
              imageAssets[metaAdForm[`creative_asset_${value}`].hash];
          }
        }
      });

      setMetaAdForm(newMetaAdForm);
      setSavedAssetsLoaded(true);
    }
  }, [
    savedAssetsLoaded,
    videoAssets,
    imageAssets,
    metaAdForm,
    setMetaAdForm,
    account_id,
    setError,
  ]);

  const CreativeAssetSelection = ({
    frame,
  }: {
    frame: typeof PLACEMENT_GROUP_OPTIONS[0];
  }): JSX.Element => {
    const { text, value } = frame;
    let asset: MetaBuyingImage | MetaBuyingVideo | null = metaAdForm[`creative_asset_${value}`];

    return (
      <div className="creativeAssetSelectionContainer">
        <h4>{text} Placement</h4>
        <div className="creativeAssetSelection">
          {!savedAssetsLoaded ? (
            <Spinner />
          ) : asset ? (
            <div className="selectedAssetWrapper">
              <div className="selectedAsset" onClick={() => handleChooseAsset(value)}>
                <img
                  alt=""
                  src={
                    (asset as MetaBuyingImage).permalink_url || (asset as MetaBuyingVideo).picture
                  }
                />
              </div>
              <div className="assetName">
                <a
                  href={
                    (asset as MetaBuyingImage).permalink_url || (asset as MetaBuyingVideo).picture
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {(asset as MetaBuyingImage).name || (asset as MetaBuyingVideo).title}
                </a>
                <Button
                  variant={ButtonFrameworkVariant.ICON_ONLY}
                  type={ButtonType.EMPTY}
                  icon={<MdDelete />}
                  onClick={() => updateFormField(`creative_asset_${value}`, null)}
                />
              </div>
            </div>
          ) : (
            <>
              <Button
                className="metaBuyingSubmitButton"
                type={ButtonType.OUTLINED}
                onClick={() => handleChooseAsset(value)}
              >
                {`Choose ${text} ${metaAdForm.creative_format
                  .charAt(0)
                  .toUpperCase()}${metaAdForm.creative_format.slice(1).toLowerCase()}`}
              </Button>{" "}
              <MissingCreativeWarning creativeType={creativeType} selectedAssets={selectedAssets} />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <MetaBuyingTabs
        tabIndex={tabIndex}
        creationTabs={creationTabs}
        validate={index => {
          if (index <= tabIndex) {
            saveNameFields(nameFields);
            setTabIndex(index);
          } else {
            validateForms(metaBuyingForm, showReviewModal, nameFields);
          }
        }}
      />
      <div className="metaBuyingCreate">
        <div className="metaBuyingContainer">
          <div className="metaBuyingCard">
            <div className="metaBuyingHeader">
              <h2>Ad</h2>
              <div className="metaBuyingNameWrapper">
                Name:
                <MetaBuyingNamePreview
                  dimensions={dimensions}
                  nameFields={nameFields}
                  namingConvention={namingConvention}
                  inheritedFields={inheritedFields}
                />
              </div>
            </div>

            <div className="metaBuyingFormBody">
              <div className="metaBuyingFormSection">
                <h3>Ad Name Construction</h3>
                <MetaBuyingNameFields
                  namingConvention={namingConvention}
                  dimensions={dimensions}
                  nameFields={nameFields}
                  segmentationDimensionId={String(segmentationDimensionId)}
                  client={client}
                  agency={business_manager}
                  setNameField={setNameField}
                  allNameFields={allNameFields}
                  disabled={!R.isNil(metaAdForm.ad_id)}
                  thisLevel={AD}
                />
              </div>

              {!R.includes(TAB_AD_SET, creationTabs) && (
                <div className="metaBuyingFormSection">
                  <h3>Ad Set</h3>
                  <Form.Group>
                    <Form.Label>Ad Set</Form.Label>
                    {showInputAdSetId ? (
                      <>
                        <InputNote
                          noteText="Can’t input Ad Set ID?"
                          buttonText="Select from dropdown instead"
                          switchInput={() => switchInput()}
                        />
                        <div className="metaBuyingSubmitInputWrapper">
                          <div className="metaBuyingSubmission">
                            {metaAdForm.adset_id && (
                              <div className="appliedSelection" onClick={removeAdSet}>
                                {metaAdForm.adset_id}
                              </div>
                            )}
                          </div>
                          <Form.Control
                            style={{ paddingTop: 10 }}
                            value={enteredAdSetId}
                            onChange={e => setEnteredAdSetId(e.target.value)}
                            disabled={metaAdForm.adset_id || adSetLoading}
                          />
                          {R.isNil(metaAdForm.adset_id) && (
                            <Button
                              className="metaBuyingSubmitButton"
                              type={ButtonType.FILLED}
                              variant={ButtonFrameworkVariant.LEADING_ICON}
                              icon={<HiPlus />}
                              onClick={() => submitAdSetId(enteredAdSetId)}
                              size="sm"
                              disabled={R.isEmpty(enteredAdSetId) || adSetLoading}
                            >
                              Submit
                            </Button>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <InputNote
                          noteText="Don’t see your ad set?"
                          buttonText="Enter Ad Set ID instead"
                          switchInput={() => switchInput()}
                        />
                        <SingleSelectMenu
                          placeholder="Ad Set"
                          menuId={ADSET_MENU_ID}
                          data={adSets}
                          nameProp="adset_name"
                          closeMenu={() => closeMenuState(ADSET_MENU_ID)}
                          showMenu={menusState[ADSET_MENU_ID]}
                          applySelection={id => applyAdSet(mapRowsByProp("id", adSets)[id])}
                          removeSelection={removeAdSet}
                          appliedSelection={metaAdForm.adset_id}
                        />
                      </>
                    )}
                  </Form.Group>
                </div>
              )}

              <div className="metaBuyingFormSection">
                <h3>Page and Account</h3>
                <Form.Group>
                  <Form.Label>Facebook Page</Form.Label>
                  <DropdownButton
                    title={metaAdForm.page_name || ""}
                    onSelect={value => {
                      updateFormField("facebook_page_id", value || "");
                      updateFormField(
                        "page_name",
                        facebookPages[facebookPages.findIndex(page => page.page_id === value)]
                          .name || ""
                      );
                    }}
                  >
                    {facebookPages.map(({ name, page_id }) => (
                      <Dropdown.Item key={page_id} eventKey={page_id}>
                        {name}
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                </Form.Group>
                {/* <Form.Group>
                  <Form.Label>Instagram Account</Form.Label>
                  <DropdownButton
                    title={instagramAccountMap ? instagramAccountMap.username : ""}
                    onSelect={value => updateFormField("instagram_account_id", value || "")}
                  >
                    {instagramAccounts.map(({ username, id }) => (
                      <Dropdown.Item key={id} eventKey={id}>
                        {username}
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                </Form.Group> */}
              </div>

              <div className="metaBuyingFormSection">
                <h3>Creative</h3>
                <Form.Group>
                  <Form.Label>Asset Type</Form.Label>
                  <div className="creativeSelection">
                    {CREATIVE_TYPES.map(({ label, value }) => (
                      <Button
                        key={value}
                        className={metaAdForm.creative_format === value ? "selected" : ""}
                        variant={ButtonFrameworkVariant.NO_ICON}
                        type={ButtonType.EMPTY}
                        onClick={() => updateFormField("creative_format", value)}
                        disabled={
                          metaAdForm.creative_asset_square ||
                          metaAdForm.creative_asset_horizontal ||
                          metaAdForm.creative_asset_vertical
                        }
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </Form.Group>
                {metaAdForm.creative_format && (
                  <Form.Group>
                    <Form.Label>Creative Asset</Form.Label>
                    {PLACEMENT_GROUP_OPTIONS.map(frame => (
                      <CreativeAssetSelection frame={frame} key={frame.value} />
                    ))}
                  </Form.Group>
                )}
                <Form.Group>
                  <Form.Label>Primary Text</Form.Label>
                  <Form.Control
                    value={metaAdForm.message}
                    onChange={e => updateFormField("message", e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Headline</Form.Label>
                  <Form.Control
                    value={metaAdForm.headline}
                    onChange={e => updateFormField("headline", e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    value={metaAdForm.description}
                    onChange={e => updateFormField("description", e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Call to Action</Form.Label>
                  <DropdownButton
                    title={
                      //@ts-ignore
                      mapLabelsByValues(CTA_OPTIONS)[metaAdForm.call_to_action_type] || ""
                      //@ts-ignore
                    }
                    onSelect={value => updateFormField("call_to_action_type", value || "")}
                  >
                    {CTA_OPTIONS.map(({ label, value }) => (
                      <Dropdown.Item key={value} eventKey={value}>
                        {label}
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Website URL</Form.Label>
                  <Form.Control
                    placeholder="https://www.tinuiti.com"
                    value={metaAdForm.url}
                    onChange={e => updateFormField("url", e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>URL Parameters</Form.Label>
                  <Form.Control
                    placeholder="?utm_test=example"
                    value={metaAdForm.url_params}
                    onChange={e => {
                      let { value } = e.target;
                      if (value.length && value.substring(0, 1) !== "?") {
                        value = `?${value}`;
                      }
                      updateFormField("url_params", value);
                    }}
                  />
                </Form.Group>
              </div>
              <MetaBuyingFooterControls
                handleReviewClick={() => validateForms(metaBuyingForm, showReviewModal, nameFields)}
              />
            </div>
          </div>

          <MetaBuyingOptionsNoteCard
            header={OPTIONS_NOTE_CARD_HEADERS[AD]}
            options={OPTIONS_NOTE_CARD_OPTIONS[AD]}
          />
        </div>
      </div>
      {!R.isNil(creativeModalType) && (
        <MetaCreativeAssetModal
          show={true}
          closeModal={() => setCreativeModalType(null)}
          defaultAssetFrame={defaultAssetFrame}
          accountID={account_id}
          isVideo={creativeModalType === VIDEO}
          adForm={metaAdForm}
          submitCreativeAssets={assets =>
            setMetaAdForm(
              R.toPairs(assets).reduce((newMetaAdForm, [frame, asset]) => {
                newMetaAdForm[`creative_asset_${frame}`] = asset;
                return newMetaAdForm;
              }, R.clone(metaAdForm))
            )
          }
        />
      )}
    </>
  );
};

export default MetaBuyingAd;
